
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 4,
  layout: 'reference',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for endpoints and webhooks associated with the Plaid Auth product.',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Auth' subtitle='API reference for Auth endpoints and webhooks' compact mdxType="Header" />
    <p>{`Retrieve bank account information to set up electronic funds transfers, such as ACH payments in the US, EFT payments in Canada, Bacs payments in the UK, and IBAN / SEPA payments in the EU.`}</p>
    <p>{`For how-to guidance, see the `}<a parentName="p" {...{
        "href": "/docs/auth/"
      }}>{`Auth documentation`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/auth/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get bank account and routing number and verification status`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/auth/verify`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Verify a user's bank account information using `}<a parentName="td" {...{
              "href": "/docs/auth/coverage/database-auth"
            }}>{`Database Auth`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/bank_transfer/event/list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Search for updates on micro-deposit verification statuses based on filter criteria`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/bank_transfer/event/sync`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get updates on micro-deposit verification statuses using a cursor`}</td>
        </tr>
      </tbody>
    </table>
    <div className="long-endpoint-table">
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`See also`}</th>
            <th parentName="tr" {...{
              "align": null
            }}></th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/processor/token/create`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Create a token for using Auth with a processing partner`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/sandbox/processor_token/create`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Create a token for testing Auth with a processing partner`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/processor/stripe/bank_account_token/create`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Create a token for using Auth with Stripe as a processing partner`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/sandbox/item/set_verification_status`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Change a Sandbox Item's micro-deposit verification status`}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Webhooks`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#default_update"
            }}><inlineCode parentName="a">{`DEFAULT_UPDATE`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Item has account(s) with updated Auth data`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#automatically_verified"
            }}><inlineCode parentName="a">{`AUTOMATICALLY_VERIFIED`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Item has been verified`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#verification_expired"
            }}><inlineCode parentName="a">{`VERIFICATION_EXPIRED`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Item verification has failed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#bank_transfers_events_update"
            }}><inlineCode parentName="a">{`BANK_TRANSFERS_EVENTS_UPDATE`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New micro-deposit verification events available`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#sms_microdeposits_verification"
            }}><inlineCode parentName="a">{`SMS_MICRODEPOSITS_VERIFICATION`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Text message verification status has changed`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "endpoints"
    }}>{`Endpoints`}</h3>
    <h4 {...{
      "id": "authget"
    }}>{`/auth/get`}</h4>
    <Schema method='POST' route='/auth/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/auth/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: AuthGetRequest = {
  access_token: accessToken,
};
try {
  const response = await plaidClient.authGet(request);
  const accountData = response.data.accounts;
  const numbers = response.data.numbers;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/auth/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": String,
  "options": {
    "account_ids": [String]
  }
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::AuthGetRequest.new({ access_token: access_token })
response = client.auth_get(request)
numbers = response.numbers
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`AuthGetRequest authGetRequest = new AuthGetRequest()
  .accessToken(accessToken);
Response<AuthGetResponse> response = client()
  .authGet(authGetRequest)
  .execute();
AuthGetNumbers numbers = response.body().getNumbers();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = AuthGetRequest(access_token=access_token)
response = client.auth_get(request)
numbers = response['numbers']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`authGetResp, _, err := client.PlaidApi.AuthGet(ctx).AuthGetRequest(
  *plaid.NewAuthGetRequest(accessToken),
).Execute()

numbers := authGetResp.GetNumbers()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`authGetResp, err := client.Auth.Get(ctx, &plaid.AuthGetRequest{AccessToken: accessToken})

numbers := authGetResp.GetNumbers()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/auth/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "authverify"
    }}>{`/auth/verify`}</h4>
    <Schema method='POST' route='/auth/verify' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/auth/verify' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: AuthVerifyRequest = {
  numbers: {
    ach: {
      account: '1234567890',
      routing: '011401533',
    },
  },
  legal_name: 'Jane Doe',
};
try {
  const response = await plaidClient.authVerify(request);
  const verificationStatus = response.data.verification_status;
  const nameMatchScore = response.data.verification_insights.name_match_score;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/auth/verify \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "numbers": {
    "ach": {
      "account": "1234567890",
      "routing": "011401533"
    }
  },
  "legal_name": "Jane Doe"
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::AuthVerifyRequest.new({
  numbers: {
    ach: {
      account: '1234567890',
      routing: '011401533',
    },
  },
  legal_name: 'Jane Doe',
})
response = client.auth_verify(request)
verification_status = response.verification_status
name_match_score = response.verification_insights.name_match_score
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`AuthVerifyRequestNumbers numbers = new AuthVerifyRequestNumbers()
  .ach(new AuthVerifyNumbersACH()
    .account("1234567890")
    .routing("011401533"));
AuthVerifyRequest authVerifyRequest = new AuthVerifyRequest()
  .numbers(numbers)
  .legalName("Jane Doe");
Response<AuthVerifyResponse> response = client()
  .authVerify(authVerifyRequest)
  .execute();
String verificationStatus = response.body().getVerificationStatus();
Integer nameMatchScore = response.body().getVerificationInsights().getNameMatchScore();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = AuthVerifyRequest(
  numbers=AuthVerifyRequestNumbers(
    ach=AuthVerifyNumbersACH(
      account='1234567890',
      routing='011401533',
    ),
  ),
  legal_name='Jane Doe',
)
response = client.auth_verify(request)
verification_status = response['verification_status']
name_match_score = response['verification_insights']['name_match_score']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`numbers := *plaid.NewAuthVerifyRequestNumbers(
  *plaid.NewAuthVerifyNumbersACH("1234567890", "011401533"),
)
request := *plaid.NewAuthVerifyRequest(numbers)
request.SetLegalName("Jane Doe")

authVerifyResp, _, err := client.PlaidApi.AuthVerify(ctx).AuthVerifyRequest(
  request,
).Execute()

verificationStatus := authVerifyResp.GetVerificationStatus()
verificationInsights := authVerifyResp.GetVerificationInsights()
nameMatchScore := verificationInsights.GetNameMatchScore()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`numbers := &plaid.AuthVerifyRequestNumbers{
    Ach: &plaid.AuthVerifyNumbersAch{
        Account: "1234567890",
        Routing: "011401533",
    },
}
request := &plaid.AuthVerifyRequest{Numbers: numbers}
request.LegalName = plaid.String("Jane Doe")

authVerifyResp, err := client.Auth.Verify(ctx, request)

verificationStatus := authVerifyResp.GetVerificationStatus()
verificationInsights := authVerifyResp.GetVerificationInsights()
nameMatchScore := verificationInsights.GetNameMatchScore()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/auth/verify' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "bank_transfereventlist"
    }}>{`/bank_transfer/event/list`}</h4>
    <Schema method='POST' route='/bank_transfer/event/list' type='request' mdxType="Schema">
      <MDXCodeBlock title='/bank_transfer/event/list' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/bank_transfer/event/list \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "start_date": String,
   "end_date": String,
   "bank_transfer_id": String,
   "account_id": String,
   "bank_transfer_type": String,
   "event_types": [String],
   "count": Number,
   "offset": Number,
   "origination_account_id": String,
   "direction": String
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = BankTransferEventListRequest(
  start_date=start_date,
  end_date=end_date,
  bank_transfer_id=bank_transfer_id,
  account_id=account_id,
  bank_transfer_type=bank_transfer_type,
  event_types=event_types,
  count=count,
  offset=offset,
  origination_account_id=origination_account_id,
  direction=direction,
)
response = client.bank_transfer_event_list(request)
events = response['bank_transfer_events']
for event in events:
  # iterate through events
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: BankTransferEventListRequest = {
  start_date: start_date,
  end_date: end_date,
  bank_transfer_id: bank_transfer_id,
  account_id: account_id,
  bank_transfer_type: bank_transfer_type,
  event_types: event_types,
  count: count,
  offset: offset,
  origination_account_id: origination_account_id,
  direction: direction,
};
try {
  const response = await plaidClient.bankTransferEventList(request);
  const events = response.data.bank_transfer_events;
  for (const event of events) {
    // iterate through events
  }
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`BankTransferEventListRequest request = new BankTransferEventListRequest()
  .bankTransferId(bankTransferId)
  .startDate(startDate)
  .endDate(endDate)
  .accountId(accountId)
  .bankTransferType(bankTransferType)
  .eventTypes(eventTypes)
  .count(count)
  .offset(offset)
  .originationAccountId(originationAccountId)
  .direction(direction);
Response<BankTransferEventListResponse> response = client()
  .bankTransferEventList(request)
  .execute();
List<BankTransferEvent> bankTransferEvents =
  response.body().getBankTransferEvents();
for (BankTransferEvent e : bankTransferEvents) {
  // iterate through events
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::BankTransferEventListRequest.new(
  {
    start_date: start_date,
    end_date: end_date,
    bank_transfer_id: bank_transfer_id,
    account_id: account_id,
    bank_transfer_type: bank_transfer_type,
    event_types: event_types,
    count: count,
    offset: offset,
    origination_account_id: origination_account_id,
    direction: direction,
  }
)
response = client.bank_transfer_event_list(request)
response.bank_transfer_events.each do |event|
  # iterate through events
end
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewBankTransferEventListRequest()
response, _, err := client.PlaidApi.BankTransferEventList(ctx).BankTransferEventListRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    banktransfer "github.com/plaid/plaid-go/v49/banktransfer"
)

request := &banktransfer.BankTransferEventListRequest{}
response, err := client.BankTransfer.Event.List(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/bank_transfer/event/list' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "bank_transfereventsync"
    }}>{`/bank_transfer/event/sync`}</h4>
    <Schema method='POST' route='/bank_transfer/event/sync' type='request' mdxType="Schema">
      <MDXCodeBlock title='/bank_transfer/event/sync' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/bank_transfer/event/sync \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "after_id": Number,
   "count": Number
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = BankTransferEventSyncRequest(
    after_id=after_id,
    count=count
)
response = client.bank_transfer_event_sync(request)
events = response["bank_transfer_events"]
for event in events:
  # iterate through events
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: BankTransferEventSyncRequest = {
  after_id: afterID,
  count: 25,
};
try {
  const response = await plaidClient.bankTransferEventSync(request);
  const events = response.data.bank_transfer_events;
  for (const event of events) {
    // iterate through events
  }
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`BankTransferEventSyncRequest request = new BankTransferEventSyncRequest()
  .afterId(0)
  .count(count);
Response<BankTransferEventSyncResponse> response = client()
  .bankTransferEventSync(request)
  .execute();
List<BankTransferEvent> bankTransferEvents =
  response.body().getBankTransferEvents();
for (BankTransferEvent e : bankTransferEvents) {
  // iterate through events
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::BankTransferEventSyncRequest.new({ after_id: 0 })
response = client.bank_transfer_event_sync(request)
response.bank_transfer_events.each do |event|
  # iterate through events
end
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewBankTransferEventSyncRequest(
  0, // after_id
)
response, _, err := client.PlaidApi.BankTransferEventSync(ctx).BankTransferEventSyncRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    banktransfer "github.com/plaid/plaid-go/v49/banktransfer"
)

request := &banktransfer.BankTransferEventSyncRequest{AfterID: 0} // after_id

response, err := client.BankTransfer.Event.Sync(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/bank_transfer/event/sync' type='response' mdxType="Schema" />
    <h3 {...{
      "id": "webhooks"
    }}>{`Webhooks`}</h3>
    <p>{`Updates are sent for Items that are linked using micro-deposits (excluding Instant Micro-deposits).`}</p>
    <p>{`When an automated micro-deposit is created, Plaid sends a webhook upon successful verification. If verification does not succeed after seven days for an automated micro-deposit, Plaid sends a `}<inlineCode parentName="p">{`VERIFICATION_EXPIRED`}</inlineCode>{` webhook. If you attempt to retrieve an automated micro-deposit Item before verification succeeds, you'll receive a response with the HTTP status code 400 and a Plaid error code of `}<inlineCode parentName="p">{`PRODUCT_NOT_READY`}</inlineCode>{`. For Same-Day micro-deposits, Plaid does not send `}<inlineCode parentName="p">{`AUTOMATICALLY_VERIFIED`}</inlineCode>{` or `}<inlineCode parentName="p">{`VERIFICATION_EXPIRED`}</inlineCode>{` webhooks, but you may instead use the `}<inlineCode parentName="p">{`BANK_TRANSFERS_EVENTS_UPDATE`}</inlineCode>{` webhook to `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/microdeposit-events/"
      }}>{`access the underlying ACH events`}</a>{` of micro-deposits.`}</p>
    <p>{`Plaid will trigger a `}<inlineCode parentName="p">{`DEFAULT_UPDATE`}</inlineCode>{` webhook for Items that undergo a change in Auth data. This is a rare event and is generally caused by data partners notifying Plaid of a change in their account numbering system or to their routing numbers. To avoid returned transactions, customers that receive a `}<inlineCode parentName="p">{`DEFAULT_UPDATE`}</inlineCode>{` webhook with the `}<inlineCode parentName="p">{`account_ids_with_updated_auth`}</inlineCode>{` object populated should immediately discontinue all usages of existing Auth data for those accounts and call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/processor/auth/get`}</inlineCode>{` to obtain updated account and routing numbers.`}</p>
    <h4 {...{
      "id": "default_update"
    }}><inlineCode parentName="h4">{`DEFAULT_UPDATE`}</inlineCode></h4>
    <Schema model='AuthDefaultUpdateWebhook' type='model' mdxType="Schema" />
    <h4 {...{
      "id": "automatically_verified"
    }}><inlineCode parentName="h4">{`AUTOMATICALLY_VERIFIED`}</inlineCode></h4>
    <Schema model='AutomaticallyVerifiedWebhook' type='model' mdxType="Schema" />
    <h4 {...{
      "id": "verification_expired"
    }}><inlineCode parentName="h4">{`VERIFICATION_EXPIRED`}</inlineCode></h4>
    <Schema model='VerificationExpiredWebhook' type='model' mdxType="Schema" />
    <h4 {...{
      "id": "bank_transfers_events_update"
    }}><inlineCode parentName="h4">{`BANK_TRANSFERS_EVENTS_UPDATE`}</inlineCode></h4>
    <Schema model='BankTransfersEventsUpdateWebhookForAuth' type='model' mdxType="Schema" />
    <h4 {...{
      "id": "sms_microdeposits_verification"
    }}><inlineCode parentName="h4">{`SMS_MICRODEPOSITS_VERIFICATION`}</inlineCode></h4>
    <Schema model='HostedMMDVerificationWebhook' type='model' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "endpoints",
        level: 3,
        title: "Endpoints",
      },
{
        id: "authget",
        level: 4,
        title: "/auth/get",
      },
{
        id: "authverify",
        level: 4,
        title: "/auth/verify",
      },
{
        id: "bank_transfereventlist",
        level: 4,
        title: "/bank_transfer/event/list",
      },
{
        id: "bank_transfereventsync",
        level: 4,
        title: "/bank_transfer/event/sync",
      },
{
        id: "webhooks",
        level: 3,
        title: "Webhooks",
      },
{
        id: "default_update",
        level: 4,
        title: "DEFAULT_UPDATE",
      },
{
        id: "automatically_verified",
        level: 4,
        title: "AUTOMATICALLY_VERIFIED",
      },
{
        id: "verification_expired",
        level: 4,
        title: "VERIFICATION_EXPIRED",
      },
{
        id: "bank_transfers_events_update",
        level: 4,
        title: "BANK_TRANSFERS_EVENTS_UPDATE",
      },
{
        id: "sms_microdeposits_verification",
        level: 4,
        title: "SMS_MICRODEPOSITS_VERIFICATION",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
